'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Box, Flex } from '@mantine/core'
import { StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'
import { useMessageCenterFeatureEnabled } from '@/contexts/AccountDashboardFeatureContext'
import { useUserContext } from '@/contexts/UserContext'
import { NavBadge } from '@/components/ui/NavBadge'
import { siteRoutes } from '@/lib/routes/site'
import { useGetConversationsUnreadCountQuery } from '@/lib/store/api/messageCenterApi'
import { useGetUpdatesUnreadCountQuery } from '@/lib/store/api/updatesApi'
import { AccountIcon } from '@/components/SiteHeader/AccountIcon'
import classes from './AccountLink.module.css'

interface AccountLinkProps {
    /**
     * True when the request already has session cookies.
     * Used so the account destination does not flash Sign in while `fetchUser` runs.
     */
    initiallyLoggedIn: boolean
}

/**
 * One visual treatment for signed-out and signed-in.
 * Destination and accessible name switch: `/login` (“Sign in”) vs `/account` (“Account”).
 */
export function AccountLink({ initiallyLoggedIn }: AccountLinkProps) {
    const { fetchUser, user } = useUserContext()
    const [hasResolvedUser, setHasResolvedUser] = useState(!initiallyLoggedIn)

    // Keep the Account dest while session cookies exist and the patient is still
    // loading. fetchUser is throttled and may return undefined; .finally runs
    // without making this effect async. After it settles, a missing user
    // falls through to Sign in.
    useEffect(() => {
        if (user || !initiallyLoggedIn) {
            setHasResolvedUser(true)
            return
        }

        const userRequest = fetchUser()
        if (!userRequest) {
            setHasResolvedUser(true)
            return
        }

        userRequest.finally(() => setHasResolvedUser(true))
    }, [fetchUser, initiallyLoggedIn, user])

    const isLoggedIn = !!user || (!hasResolvedUser && initiallyLoggedIn)
    const label = isLoggedIn ? 'Account' : 'Sign in'
    const messageCenterEnabled = useMessageCenterFeatureEnabled()
    // currentData, unlike data, clears while skipped so a cached count never outlives sign-out.
    const { currentData: messagesUnreadData } = useGetConversationsUnreadCountQuery(undefined, {
        skip: !isLoggedIn || !messageCenterEnabled,
    })
    const { currentData: updatesUnreadData } = useGetUpdatesUnreadCountQuery(undefined, {
        skip: !isLoggedIn,
    })
    const unreadCount =
        (messagesUnreadData?.unread_count ?? 0) + (updatesUnreadData?.unread_count ?? 0)

    return (
        <Link
            href={isLoggedIn ? siteRoutes.account : siteRoutes.login}
            prefetch
            aria-label={unreadCount > 0 ? `${label}, ${unreadCount} unread` : label}
            title={label}
            className={classes.accountLink}
            data-testid={isLoggedIn ? 'account-button' : 'signin-button'}
            onClick={() =>
                trackStoreEvent(StoreAnalytics.NAV_HEADER_CLICKED, {
                    name: isLoggedIn ? 'account' : 'sign-in',
                })
            }
        >
            <Flex pos="relative">
                <AccountIcon />
                {unreadCount > 0 && (
                    <Box pos="absolute" top={-4} right={-4}>
                        <NavBadge count={unreadCount} size={18} testId="account-link-unread-badge" />
                    </Box>
                )}
            </Flex>
        </Link>
    )
}
